/**
 * Channel notification override utilities
 */

export type ChannelNotifyLevel = "all" | "mentions" | "mute";

export interface ChannelOverride {
  user_id: string;
  channel_id: string;
  level: ChannelNotifyLevel;
  desktop_level?: ChannelNotifyLevel | null;
  mobile_level?: ChannelNotifyLevel | null;
}

export interface NotificationContext {
  type: "message" | "mention" | "direct_mention" | "dm";
  isChannelMuted: boolean;
  channelOverride?: ChannelOverride | null;
  globalLevel: string;
}

/**
 * Determine effective notification level for a channel
 * Channel override takes priority over global setting
 */
export function getEffectiveLevel(
  globalLevel: string,
  channelOverride?: ChannelOverride | null,
  device?: "desktop" | "mobile",
): ChannelNotifyLevel {
  if (!channelOverride) {
    // Map global level to channel level
    return globalLevel === "everything"
      ? "all"
      : globalLevel === "nothing"
        ? "mute"
        : "mentions";
  }

  // Check device-specific override first
  if (device === "desktop" && channelOverride.desktop_level) {
    return channelOverride.desktop_level;
  }
  if (device === "mobile" && channelOverride.mobile_level) {
    return channelOverride.mobile_level;
  }

  // Use channel-level override
  return channelOverride.level;
}

/**
 * Check if notification should be sent based on channel override
 */
export function shouldNotifyForChannel(
  context: NotificationContext,
  device?: "desktop" | "mobile",
): boolean {
  const level = getEffectiveLevel(
    context.globalLevel,
    context.channelOverride,
    device,
  );

  // 'all' notifies on all channel posts
  if (level === "all") {
    return true;
  }

  // 'mentions' notifies on mentions only
  if (level === "mentions") {
    return context.type === "mention" || context.type === "direct_mention";
  }

  // 'mute' suppresses all except direct @mentions
  if (level === "mute") {
    return context.type === "direct_mention";
  }

  return false;
}

/**
 * Check if badge should show for muted channel
 * Direct @mention badges muted channels, @channel/@here do not
 */
export function shouldBadgeMutedChannel(
  mentionType: "direct" | "channel" | "here" | "everyone",
): boolean {
  // Muted channel still badges on direct @mention
  if (mentionType === "direct") {
    return true;
  }

  // @channel/@here don't badge muted channels
  return false;
}

/**
 * Create a channel override
 */
export function createChannelOverride(
  userId: string,
  channelId: string,
  level: ChannelNotifyLevel,
  desktopLevel?: ChannelNotifyLevel,
  mobileLevel?: ChannelNotifyLevel,
): ChannelOverride {
  return {
    user_id: userId,
    channel_id: channelId,
    level,
    desktop_level: desktopLevel || null,
    mobile_level: mobileLevel || null,
  };
}

/**
 * Remove channel override (reverts to global setting)
 */
export function removeChannelOverride(
  overrides: ChannelOverride[],
  channelId: string,
): ChannelOverride[] {
  return overrides.filter((o) => o.channel_id !== channelId);
}
